'use client';

import React, { useState } from 'react';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface RoleVersion {
  version: string;
  timestamp: number;
  author: string;
  changes: string;
  attribution: string;
}

interface PublishedRole {
  id: string;
  baseRole: string;
  customLabel: string;
  vocalLens: string;
  description: string;
  amanahDomain: string;
  irlEntry: string;
  keysEngaged: string[];
  mrgDomains: string[];
  author: string;
  attribution: string;
  isnadChain: string[];
  version: string;
  versions: RoleVersion[];
  status: 'draft' | 'submitted' | 'community-review' | 'published';
  remixCount: number;
  remixedFrom?: string;
  customFields: Record<string, string>;
  lineageColor: string;
}

// ─── SEED ROLES ───────────────────────────────────────────────────────────────

const SEED_ROLES = [
  { id: 'engineer', label: 'Engineer', color: '#2d8a87', icon: '⚙', arabic: 'مُهَنْدِس', desc: 'Systems, provision, and the architecture of creation', irlEntry: 'Systems decomposition — what are the inputs, processes, outputs?', keysDefault: ['Key 04: Mizan — The Balance', 'Key 05: Amanah — The Trust'] },
  { id: 'teacher', label: 'Teacher', color: '#8a8ab8', icon: '✦', arabic: 'مُعَلِّم', desc: 'Curriculum, transmission, and the Ilm that flows', irlEntry: 'Curriculum analysis — what Names are embedded in what you teach?', keysDefault: ['Key 07: Yaqeen — Certainty', 'Key 01: Ayat — Signs in Creation'] },
  { id: 'scientist', label: 'Scientist', color: '#2d7a47', icon: '◎', arabic: 'عَالِم', desc: 'Research, observation, and the Taqdir of measurement', irlEntry: 'Fractal branching — each branch a fission, each node a Name recognized', keysDefault: ['Key 01: Ayat — Signs in Creation', 'Key 03: Taqwah — Consciousness'] },
  { id: 'farmer', label: 'Farmer / Gardener', color: '#D4A82A', icon: '⬡', arabic: 'فَلَّاح', desc: 'Soil, growth, and the Sunnat of cultivation', irlEntry: 'Soil analysis — what Names are active in the growth cycle?', keysDefault: ['Key 04: Mizan — The Balance', 'Key 06: Falah — Flourishing'] },
  { id: 'native', label: 'Native / Indigenous', color: '#c9a84c', icon: '◈', arabic: 'أَصِيل', desc: 'Land stewardship, ancestral knowledge, and Amanah', irlEntry: 'Root system — ancestral memory as living pattern', keysDefault: ['Key 05: Amanah — The Trust', 'Key 01: Ayat — Signs in Creation'] },
  { id: 'returning-citizen', label: 'Returning Citizen', color: '#5a5a8a', icon: '⌘', arabic: 'عَائِد', desc: 'Mizan, restoration, and the Islah of community', irlEntry: 'Mizan analysis — where is the balance transgressed? Map the restoration needed.', keysDefault: ['Key 04: Mizan — The Balance', 'Key 05: Amanah — The Trust'] },
];

const KEYS_OPTIONS = [
  'Key 01: Ayat — Signs in Creation',
  'Key 02: Haal — States of Being',
  'Key 03: Taqwah — Consciousness',
  'Key 04: Mizan — The Balance',
  'Key 05: Amanah — The Trust',
  'Key 06: Falah — Flourishing',
  'Key 07: Yaqeen — Certainty',
];

const MRG_DOMAIN_OPTIONS = [
  'Housing & Shelter',
  'Food & Nutrition',
  'Education & Literacy',
  'Health & Wellness',
  'Legal & Justice',
  'Employment & Livelihood',
  'Environmental Stewardship',
  'Community Infrastructure',
  'Youth Development',
  'Reentry & Restoration',
];

const AMANAH_DOMAIN_OPTIONS = [
  'Infrastructure Amanah — building systems that serve',
  'Knowledge Amanah — transmitting what was given',
  'Land Amanah — stewarding what was entrusted',
  'Community Amanah — bridging what is broken',
  'Restoration Amanah — healing what was harmed',
  'Provision Amanah — distributing what was sent down',
  'Observation Amanah — reading what was written',
  'Reform Amanah — proposing what the Mizan calls for',
];

// ─── COMMUNITY LIBRARY ────────────────────────────────────────────────────────

const COMMUNITY_LIBRARY: PublishedRole[] = [
  {
    id: 'pub-1',
    baseRole: 'engineer',
    customLabel: 'Water Systems Engineer',
    vocalLens: 'Water infrastructure as Amanah — every pipe is a trust, every gap a taqdir to address',
    description: 'Applies engineering methodology to water access equity in underserved wards. The water access rate is a taqdir — Ar-Razzaq\'s provision exists; the distribution system is the Amanah.',
    amanahDomain: 'Infrastructure Amanah — building systems that serve',
    irlEntry: '/inner-reality-lens',
    keysEngaged: ['Key 04: Mizan — The Balance', 'Key 05: Amanah — The Trust'],
    mrgDomains: ['Housing & Shelter', 'Environmental Stewardship'],
    author: 'Caretaker-Chicago-01',
    attribution: 'Remixed from Engineer seed role by Caretaker-Chicago-01, 2026',
    isnadChain: ['Engineer seed role (Haal Project)', 'Caretaker-Chicago-01 (Water Systems specialization)', 'Caretaker-Detroit-03 (Detroit wetlands context)'],
    version: '1.2',
    versions: [
      { version: '1.0', timestamp: 1700000000, author: 'Caretaker-Chicago-01', changes: 'Initial submission — water systems focus', attribution: 'Original remix from Engineer seed' },
      { version: '1.1', timestamp: 1700100000, author: 'Caretaker-Chicago-01', changes: 'Added MRG domain mapping and taqdir framing', attribution: 'Self-revision' },
      { version: '1.2', timestamp: 1700200000, author: 'Caretaker-Detroit-03', changes: 'Added Detroit wetlands context and Al-Latif connection', attribution: 'Community remix by Caretaker-Detroit-03' },
    ],
    status: 'published',
    remixCount: 3,
    remixedFrom: 'engineer',
    customFields: { 'Ward Focus': 'Roseland, Chicago', 'Primary Sequence': 'S4 Mycorrhizal Network', 'Taqdir Metric': 'Water access rate' },
    lineageColor: '#2d8a87',
  },
  {
    id: 'pub-2',
    baseRole: 'returning-citizen',
    customLabel: 'Restorative Justice Facilitator',
    vocalLens: 'Mizan as the framework for community healing — the returning citizen carries knowledge no other perspective has',
    description: 'Applies Islah methodology to restorative justice circles and community repair. The recidivism rate is a taqdir — a measure of what the system produces when Islah is absent.',
    amanahDomain: 'Restoration Amanah — healing what was harmed',
    irlEntry: '/inner-reality-lens',
    keysEngaged: ['Key 04: Mizan — The Balance', 'Key 02: Haal — States of Being'],
    mrgDomains: ['Legal & Justice', 'Reentry & Restoration', 'Community Infrastructure'],
    author: 'Caretaker-Atlanta-07',
    attribution: 'Remixed from Returning Citizen seed role by Caretaker-Atlanta-07, 2026',
    isnadChain: ['Returning Citizen seed role (Haal Project)', 'Caretaker-Atlanta-07 (Restorative Justice specialization)', 'Community-Review (Islah framework expansion)'],
    version: '2.0',
    versions: [
      { version: '1.0', timestamp: 1700300000, author: 'Caretaker-Atlanta-07', changes: 'Initial submission — restorative justice focus', attribution: 'Original remix from Returning Citizen seed' },
      { version: '2.0', timestamp: 1700500000, author: 'Community-Review', changes: 'Expanded Islah framework, added Quran 49:9 grounding, taqdir framing on recidivism rate', attribution: 'Community review revision' },
    ],
    status: 'published',
    remixCount: 7,
    remixedFrom: 'returning-citizen',
    customFields: { 'Ward Focus': 'Vine City, Atlanta', 'Primary Sequence': 'S1 Seasonal Cycle', 'Taqdir Metric': 'Recidivism rate' },
    lineageColor: '#5a5a8a',
  },
  {
    id: 'pub-3',
    baseRole: 'farmer',
    customLabel: 'Urban Food Forest Cultivator',
    vocalLens: 'The food desert is a taqdir — Ar-Razzaq\'s provision exists; the farmer\'s Amanah is to grow the bridge',
    description: 'Applies cultivation methodology to urban food desert restoration. Every vacant lot is a potential food forest — the soil\'s fertility is a taqdir waiting to be read.',
    amanahDomain: 'Provision Amanah — distributing what was sent down',
    irlEntry: '/inner-reality-lens',
    keysEngaged: ['Key 06: Falah — Flourishing', 'Key 04: Mizan — The Balance'],
    mrgDomains: ['Food & Nutrition', 'Environmental Stewardship', 'Community Infrastructure'],
    author: 'Caretaker-Detroit-02',
    attribution: 'Remixed from Farmer seed role by Caretaker-Detroit-02, 2026',
    isnadChain: ['Farmer seed role (Haal Project)', 'Caretaker-Detroit-02 (Urban food forest specialization)'],
    version: '1.0',
    versions: [
      { version: '1.0', timestamp: 1700600000, author: 'Caretaker-Detroit-02', changes: 'Initial submission — urban food forest focus', attribution: 'Original remix from Farmer seed' },
    ],
    status: 'community-review',
    remixCount: 1,
    remixedFrom: 'farmer',
    customFields: { 'Ward Focus': 'North End, Detroit', 'Primary Sequence': 'S3 Canopy Photosynthesis', 'Taqdir Metric': 'Food desert coverage' },
    lineageColor: '#D4A82A',
  },
];

// ─── STEP INDICATOR ───────────────────────────────────────────────────────────

function StepIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  const steps = [
    { n: 1, label: 'Base Role' },
    { n: 2, label: 'Vocational Lens' },
    { n: 3, label: 'Keys & MRG' },
    { n: 4, label: 'Isnad & Submit' },
  ];

  return (
    <div className="flex items-center gap-0 mb-8">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex flex-col items-center">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center font-mono text-[9px] font-black transition-all"
              style={{
                background: currentStep >= s.n ? (currentStep === s.n ? '#c9a84c' : 'rgba(201,168,76,0.2)') : 'rgba(61,54,39,0.3)',
                border: `1px solid ${currentStep >= s.n ? 'rgba(201,168,76,0.5)' : 'rgba(61,54,39,0.3)'}`,
                color: currentStep === s.n ? '#060d12' : currentStep > s.n ? '#c9a84c' : 'rgba(212,197,169,0.3)',
              }}
            >
              {currentStep > s.n ? '✓' : s.n}
            </div>
            <span className="font-mono text-[7px] uppercase tracking-widest mt-1 text-center" style={{ color: currentStep >= s.n ? 'rgba(201,168,76,0.6)' : 'rgba(212,197,169,0.25)', maxWidth: 48 }}>{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className="flex-1 h-px mx-1 mb-4" style={{ background: currentStep > s.n ? 'rgba(201,168,76,0.3)' : 'rgba(61,54,39,0.3)' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── PUBLISHING FORM ─────────────────────────────────────────────────────────

export function RolePublishingForm() {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [selectedBase, setSelectedBase] = useState<string>('');
  const [customLabel, setCustomLabel] = useState('');
  const [vocalLens, setVocalLens] = useState('');
  const [description, setDescription] = useState('');
  const [amanahDomain, setAmanahDomain] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedMRG, setSelectedMRG] = useState<string[]>([]);
  const [authorName, setAuthorName] = useState('');
  const [isnadChain, setIsnadChain] = useState<string[]>([]);
  const [isnadEntry, setIsnadEntry] = useState('');
  const [customFieldKey, setCustomFieldKey] = useState('');
  const [customFieldValue, setCustomFieldValue] = useState('');
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isRemix, setIsRemix] = useState(false);

  const baseRoleData = SEED_ROLES.find(r => r.id === selectedBase);

  const addCustomField = () => {
    if (!customFieldKey.trim() || !customFieldValue.trim()) return;
    setCustomFields(prev => ({ ...prev, [customFieldKey.trim()]: customFieldValue.trim() }));
    setCustomFieldKey('');
    setCustomFieldValue('');
  };

  const toggleKey = (key: string) => {
    setSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleMRG = (domain: string) => {
    setSelectedMRG(prev => prev.includes(domain) ? prev.filter(d => d !== domain) : [...prev, domain]);
  };

  const addIsnadEntry = () => {
    if (!isnadEntry.trim()) return;
    setIsnadChain(prev => [...prev, isnadEntry.trim()]);
    setIsnadEntry('');
  };

  const removeIsnadEntry = (idx: number) => {
    setIsnadChain(prev => prev.filter((_, i) => i !== idx));
  };

  // Auto-populate Isnad chain root when base role is selected
  const handleBaseRoleSelect = (roleId: string) => {
    setSelectedBase(roleId);
    const role = SEED_ROLES.find(r => r.id === roleId);
    if (role) {
      setIsnadChain([`${role.label} seed role (Haal Project)`]);
      setSelectedKeys(role.keysDefault);
    }
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="rounded-2xl p-8 text-center" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)' }}>
        <div className="text-3xl mb-4" style={{ color: '#2d7a47' }}>◎</div>
        <h3 className="font-mono text-lg font-black uppercase tracking-wide mb-2" style={{ color: '#F5F0E8' }}>
          Submitted to Community Library
        </h3>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, marginBottom: 16, lineHeight: 1.7 }}>
          Your role lens has been submitted for community review. It will be visible in the library once reviewed by senior caretakers. Version 1.0 is now in the archive.
        </p>
        <div className="rounded-xl p-4 mb-4 text-left" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>Isnad Attribution Record</p>
          <div className="space-y-1">
            {isnadChain.map((entry, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="font-mono text-[8px]" style={{ color: 'rgba(201,168,76,0.4)' }}>{i + 1}.</span>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 11 }}>{entry}</p>
              </div>
            ))}
            {authorName && (
              <div className="flex items-center gap-2">
                <span className="font-mono text-[8px]" style={{ color: 'rgba(201,168,76,0.4)' }}>{isnadChain.length + 1}.</span>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 11 }}>{authorName} ({customLabel || selectedBase} specialization, {new Date().getFullYear()})</p>
              </div>
            )}
          </div>
        </div>
        <div className="rounded-xl p-4 mb-6 text-left" style={{ background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)' }}>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(45,122,71,0.5)' }}>Version Control</p>
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11 }}>v1.0 — Initial submission. Community remixes increment the minor version (v1.1, v1.2). Community review revisions increment the major version (v2.0). All versions are preserved in the archive.</p>
        </div>
        <button
          onClick={() => { setSubmitted(false); setStep(1); setSelectedBase(''); setCustomLabel(''); setVocalLens(''); setDescription(''); setIsnadChain([]); setSelectedKeys([]); setSelectedMRG([]); setCustomFields({}); }}
          className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded transition-all"
          style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', background: 'rgba(201,168,76,0.04)' }}
        >
          Submit Another Role
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(6,13,18,0.97)', border: '1px solid rgba(201,168,76,0.15)' }}>
      <div className="px-6 py-5 border-b" style={{ borderColor: 'rgba(201,168,76,0.1)', background: 'rgba(201,168,76,0.03)' }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>◈ 4-Step Caretaker Publishing Form</p>
        <h3 className="font-mono text-lg font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>Remix a Seed Role</h3>
        <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, marginTop: 4 }}>
          "What if we modeled all the possible roles of our current society, redeveloping and recognizing those roles with the primary purpose being oriented toward the remembrance of Allah?" — Al-Qadir Section
        </p>
      </div>

      <div className="p-6">
        <StepIndicator currentStep={step} totalSteps={4} />

        {/* ── STEP 1: BASE ROLE SELECTION ── */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.6)' }}>Step 1 of 4</p>
              <h4 className="font-mono text-base font-black uppercase tracking-tight mb-1" style={{ color: '#F5F0E8' }}>Select a Base Role</h4>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, lineHeight: 1.6 }}>
                Choose one of the 6 seed roles to remix. Your role will inherit the base role's IRL entry sequence, Keys path, and MRG bridge — then you'll customize it in the next steps.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SEED_ROLES.map(role => (
                <button
                  key={role.id}
                  onClick={() => handleBaseRoleSelect(role.id)}
                  className="rounded-xl p-4 text-left transition-all"
                  style={{
                    background: selectedBase === role.id ? `${role.color}12` : `${role.color}05`,
                    border: `1px solid ${selectedBase === role.id ? `${role.color}45` : `${role.color}18`}`,
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span style={{ color: role.color, fontSize: 16 }}>{role.icon}</span>
                    <p className="font-mono text-[9px] font-black uppercase tracking-widest" style={{ color: role.color }}>{role.label}</p>
                    <p style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: `${role.color}50`, direction: 'rtl', marginLeft: 'auto' }}>{role.arabic}</p>
                  </div>
                  <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 10, lineHeight: 1.5, marginBottom: 6 }}>{role.desc}</p>
                  <p style={{ color: `${role.color}60`, fontSize: 9, lineHeight: 1.5, fontStyle: 'italic' }}>IRL Entry: {role.irlEntry}</p>
                  {selectedBase === role.id && (
                    <div className="mt-2 flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: role.color }} />
                      <span className="font-mono text-[7px] uppercase" style={{ color: role.color }}>Selected</span>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Remix toggle */}
            <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(61,54,39,0.15)', border: '1px solid rgba(61,54,39,0.3)' }}>
              <button
                onClick={() => setIsRemix(v => !v)}
                className="w-8 h-4 rounded-full transition-all relative flex-shrink-0"
                style={{ background: isRemix ? '#c9a84c' : 'rgba(61,54,39,0.5)' }}
              >
                <div className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all" style={{ left: isRemix ? '18px' : '2px' }} />
              </button>
              <div>
                <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: isRemix ? '#c9a84c' : 'rgba(212,197,169,0.4)' }}>Remixing an existing community role</p>
                <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 9 }}>Toggle on if you are building on a published community role rather than a seed role</p>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setStep(2)}
                disabled={!selectedBase}
                className="font-mono text-[9px] uppercase tracking-widest px-5 py-2 rounded transition-all"
                style={{
                  background: selectedBase ? 'rgba(201,168,76,0.1)' : 'rgba(61,54,39,0.2)',
                  border: `1px solid ${selectedBase ? 'rgba(201,168,76,0.4)' : 'rgba(61,54,39,0.3)'}`,
                  color: selectedBase ? '#c9a84c' : 'rgba(212,197,169,0.25)',
                  cursor: selectedBase ? 'pointer' : 'not-allowed',
                }}
              >
                Next: Vocational Lens →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: VOCATIONAL LENS & AMANAH DOMAIN ── */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.6)' }}>Step 2 of 4</p>
              <h4 className="font-mono text-base font-black uppercase tracking-tight mb-1" style={{ color: '#F5F0E8' }}>Custom Vocational Lens & Amanah Domain</h4>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, lineHeight: 1.6 }}>
                Name your specific vocation and define the lens through which you apply the {baseRoleData?.label} methodology. What does your work look like when oriented toward remembrance?
              </p>
            </div>

            {/* Base role reminder */}
            {baseRoleData && (
              <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: `${baseRoleData.color}08`, border: `1px solid ${baseRoleData.color}20` }}>
                <span style={{ color: baseRoleData.color, fontSize: 16 }}>{baseRoleData.icon}</span>
                <div>
                  <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: `${baseRoleData.color}70` }}>Remixing from</p>
                  <p className="font-mono text-[10px] font-black uppercase" style={{ color: '#d4c5a9' }}>{baseRoleData.label} seed role</p>
                </div>
              </div>
            )}

            {/* Custom label */}
            <div>
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>Custom Role Label *</label>
              <input
                type="text"
                value={customLabel}
                onChange={e => setCustomLabel(e.target.value)}
                placeholder={`e.g., Water Systems ${baseRoleData?.label || 'Engineer'}`}
                className="w-full rounded-lg px-3 py-2 font-mono text-sm outline-none transition-all"
                style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(61,54,39,0.4)', color: '#d4c5a9' }}
              />
            </div>

            {/* Vocational lens */}
            <div>
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>Vocational Lens Statement *</label>
              <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 9, marginBottom: 6 }}>How does your specific work reframe the {baseRoleData?.label || 'base'} role through the lens of remembrance? What taqdir does your work address?</p>
              <textarea
                value={vocalLens}
                onChange={e => setVocalLens(e.target.value)}
                placeholder={`e.g., "${baseRoleData?.label || 'Engineering'} as Amanah — every [specific work] is a trust, every gap a taqdir to address"`}
                rows={3}
                className="w-full rounded-lg px-3 py-2 font-mono text-sm outline-none transition-all resize-none"
                style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(61,54,39,0.4)', color: '#d4c5a9' }}
              />
            </div>

            {/* Description */}
            <div>
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>Role Description</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe how this role applies the methodology in practice. What ward context? What specific work?"
                rows={3}
                className="w-full rounded-lg px-3 py-2 font-mono text-sm outline-none transition-all resize-none"
                style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(61,54,39,0.4)', color: '#d4c5a9' }}
              />
            </div>

            {/* Amanah domain */}
            <div>
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>Primary Amanah Domain *</label>
              <div className="grid grid-cols-1 gap-2">
                {AMANAH_DOMAIN_OPTIONS.map(domain => (
                  <button
                    key={domain}
                    onClick={() => setAmanahDomain(domain)}
                    className="text-left px-3 py-2 rounded-lg transition-all font-mono text-[9px]"
                    style={{
                      background: amanahDomain === domain ? 'rgba(201,168,76,0.1)' : 'rgba(6,13,18,0.5)',
                      border: `1px solid ${amanahDomain === domain ? 'rgba(201,168,76,0.4)' : 'rgba(61,54,39,0.3)'}`,
                      color: amanahDomain === domain ? '#c9a84c' : 'rgba(212,197,169,0.45)',
                    }}
                  >
                    {domain}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom fields */}
            <div>
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>Custom Context Fields</label>
              <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 9, marginBottom: 6 }}>Add ward focus, primary sequence, taqdir metric, or any other context-specific field.</p>
              <div className="flex gap-2 mb-2">
                <input type="text" value={customFieldKey} onChange={e => setCustomFieldKey(e.target.value)} placeholder="Field name (e.g. Ward Focus)" className="flex-1 rounded px-2 py-1.5 font-mono text-[10px] outline-none" style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(61,54,39,0.4)', color: '#d4c5a9' }} />
                <input type="text" value={customFieldValue} onChange={e => setCustomFieldValue(e.target.value)} placeholder="Value" className="flex-1 rounded px-2 py-1.5 font-mono text-[10px] outline-none" style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(61,54,39,0.4)', color: '#d4c5a9' }} />
                <button onClick={addCustomField} className="px-3 py-1.5 rounded font-mono text-[9px] uppercase" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c', cursor: 'pointer' }}>Add</button>
              </div>
              {Object.entries(customFields).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 mb-1 px-2 py-1 rounded" style={{ background: 'rgba(61,54,39,0.15)', border: '1px solid rgba(61,54,39,0.25)' }}>
                  <span className="font-mono text-[8px]" style={{ color: 'rgba(201,168,76,0.5)' }}>{k}:</span>
                  <span className="font-mono text-[8px]" style={{ color: 'rgba(212,197,169,0.6)' }}>{v}</span>
                </div>
              ))}
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded" style={{ border: '1px solid rgba(61,54,39,0.3)', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', background: 'transparent' }}>← Back</button>
              <button
                onClick={() => setStep(3)}
                disabled={!customLabel || !vocalLens || !amanahDomain}
                className="font-mono text-[9px] uppercase tracking-widest px-5 py-2 rounded transition-all"
                style={{
                  background: (customLabel && vocalLens && amanahDomain) ? 'rgba(201,168,76,0.1)' : 'rgba(61,54,39,0.2)',
                  border: `1px solid ${(customLabel && vocalLens && amanahDomain) ? 'rgba(201,168,76,0.4)' : 'rgba(61,54,39,0.3)'}`,
                  color: (customLabel && vocalLens && amanahDomain) ? '#c9a84c' : 'rgba(212,197,169,0.25)',
                  cursor: (customLabel && vocalLens && amanahDomain) ? 'pointer' : 'not-allowed',
                }}
              >
                Next: Keys & MRG →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: KEYS & MRG BRIDGE SELECTION ── */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.6)' }}>Step 3 of 4</p>
              <h4 className="font-mono text-base font-black uppercase tracking-tight mb-1" style={{ color: '#F5F0E8' }}>Keys & MRG Bridge Selection</h4>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, lineHeight: 1.6 }}>
                Select the Keys to Kulli Haal that govern this role's practice, and the MRG domains this role bridges. These selections determine how the role appears in the community library.
              </p>
            </div>

            {/* Keys selection */}
            <div>
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>Keys Engaged (select all that apply)</label>
              <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 9, marginBottom: 6 }}>Pre-selected from base role. Adjust as needed for your specific vocational lens.</p>
              <div className="space-y-2">
                {KEYS_OPTIONS.map(key => (
                  <button
                    key={key}
                    onClick={() => toggleKey(key)}
                    className="w-full text-left px-3 py-2 rounded-lg transition-all font-mono text-[9px] flex items-center gap-2"
                    style={{
                      background: selectedKeys.includes(key) ? 'rgba(201,168,76,0.08)' : 'rgba(6,13,18,0.5)',
                      border: `1px solid ${selectedKeys.includes(key) ? 'rgba(201,168,76,0.35)' : 'rgba(61,54,39,0.3)'}`,
                      color: selectedKeys.includes(key) ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                    }}
                  >
                    <div className="w-3 h-3 rounded flex-shrink-0 flex items-center justify-center" style={{ background: selectedKeys.includes(key) ? '#c9a84c' : 'transparent', border: `1px solid ${selectedKeys.includes(key) ? '#c9a84c' : 'rgba(61,54,39,0.5)'}` }}>
                      {selectedKeys.includes(key) && <span style={{ fontSize: 7, color: '#060d12' }}>✓</span>}
                    </div>
                    {key}
                  </button>
                ))}
              </div>
            </div>

            {/* MRG domains */}
            <div>
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>MRG Bridge Domains (select all that apply)</label>
              <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 9, marginBottom: 6 }}>Which MRG resource domains does this role bridge? These determine which ward data metrics appear as taqdir in the role's dashboard.</p>
              <div className="grid grid-cols-2 gap-2">
                {MRG_DOMAIN_OPTIONS.map(domain => (
                  <button
                    key={domain}
                    onClick={() => toggleMRG(domain)}
                    className="text-left px-3 py-2 rounded-lg transition-all font-mono text-[9px] flex items-center gap-2"
                    style={{
                      background: selectedMRG.includes(domain) ? 'rgba(45,122,71,0.08)' : 'rgba(6,13,18,0.5)',
                      border: `1px solid ${selectedMRG.includes(domain) ? 'rgba(45,122,71,0.35)' : 'rgba(61,54,39,0.3)'}`,
                      color: selectedMRG.includes(domain) ? '#2d7a47' : 'rgba(212,197,169,0.4)',
                    }}
                  >
                    <div className="w-3 h-3 rounded flex-shrink-0 flex items-center justify-center" style={{ background: selectedMRG.includes(domain) ? '#2d7a47' : 'transparent', border: `1px solid ${selectedMRG.includes(domain) ? '#2d7a47' : 'rgba(61,54,39,0.5)'}` }}>
                      {selectedMRG.includes(domain) && <span style={{ fontSize: 7, color: '#060d12' }}>✓</span>}
                    </div>
                    {domain}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded" style={{ border: '1px solid rgba(61,54,39,0.3)', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', background: 'transparent' }}>← Back</button>
              <button
                onClick={() => setStep(4)}
                disabled={selectedKeys.length === 0}
                className="font-mono text-[9px] uppercase tracking-widest px-5 py-2 rounded transition-all"
                style={{
                  background: selectedKeys.length > 0 ? 'rgba(201,168,76,0.1)' : 'rgba(61,54,39,0.2)',
                  border: `1px solid ${selectedKeys.length > 0 ? 'rgba(201,168,76,0.4)' : 'rgba(61,54,39,0.3)'}`,
                  color: selectedKeys.length > 0 ? '#c9a84c' : 'rgba(212,197,169,0.25)',
                  cursor: selectedKeys.length > 0 ? 'pointer' : 'not-allowed',
                }}
              >
                Next: Isnad & Submit →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 4: ISNAD ATTRIBUTION CHAIN & SUBMISSION ── */}
        {step === 4 && (
          <div className="space-y-5">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.6)' }}>Step 4 of 4</p>
              <h4 className="font-mono text-base font-black uppercase tracking-tight mb-1" style={{ color: '#F5F0E8' }}>Isnad Attribution Chain & Submission</h4>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, lineHeight: 1.6 }}>
                The Isnad is the chain of transmission — who saw it, when, from what vantage, and who verified. This is the Islamic epistemological standard applied to role knowledge. Your role's Isnad begins with the seed role and ends with you.
              </p>
            </div>

            {/* Isnad chain */}
            <div>
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>Isnad Chain</label>
              <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 9, marginBottom: 8 }}>
                The chain begins with the seed role (auto-populated). Add any intermediate sources — teachers, community members, published roles you built on. Your name will be added as the final link.
              </p>
              <div className="space-y-2 mb-3">
                {isnadChain.map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
                    <span className="font-mono text-[8px] font-black" style={{ color: 'rgba(201,168,76,0.5)', minWidth: 16 }}>{i + 1}.</span>
                    <p className="flex-1 font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.7)' }}>{entry}</p>
                    {i > 0 && (
                      <button onClick={() => removeIsnadEntry(i)} className="font-mono text-[8px]" style={{ color: 'rgba(212,100,50,0.5)', cursor: 'pointer', background: 'none', border: 'none' }}>✕</button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={isnadEntry}
                  onChange={e => setIsnadEntry(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addIsnadEntry()}
                  placeholder="Add a source (teacher, community member, published role...)"
                  className="flex-1 rounded px-3 py-2 font-mono text-[10px] outline-none"
                  style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(61,54,39,0.4)', color: '#d4c5a9' }}
                />
                <button onClick={addIsnadEntry} className="px-3 py-2 rounded font-mono text-[9px] uppercase" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c', cursor: 'pointer' }}>Add</button>
              </div>
            </div>

            {/* Author name */}
            <div>
              <label className="font-mono text-[8px] uppercase tracking-widest block mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>Your Caretaker Name / Handle *</label>
              <input
                type="text"
                value={authorName}
                onChange={e => setAuthorName(e.target.value)}
                placeholder="e.g., Caretaker-Chicago-01 or your chosen handle"
                className="w-full rounded-lg px-3 py-2 font-mono text-sm outline-none"
                style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(61,54,39,0.4)', color: '#d4c5a9' }}
              />
            </div>

            {/* Summary review */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(61,54,39,0.3)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.35)' }}>Submission Summary</p>
              <div className="space-y-2">
                {[
                  { label: 'Base Role', value: baseRoleData?.label || '—' },
                  { label: 'Custom Label', value: customLabel || '—' },
                  { label: 'Vocational Lens', value: vocalLens ? vocalLens.slice(0, 60) + (vocalLens.length > 60 ? '...' : '') : '—' },
                  { label: 'Amanah Domain', value: amanahDomain ? amanahDomain.split(' — ')[0] : '—' },
                  { label: 'Keys Engaged', value: selectedKeys.length > 0 ? `${selectedKeys.length} Keys` : '—' },
                  { label: 'MRG Domains', value: selectedMRG.length > 0 ? `${selectedMRG.length} domains` : '—' },
                  { label: 'Isnad Length', value: `${isnadChain.length + (authorName ? 1 : 0)} links` },
                ].map(item => (
                  <div key={item.label} className="flex items-start gap-3">
                    <span className="font-mono text-[8px] uppercase tracking-widest flex-shrink-0" style={{ color: 'rgba(212,197,169,0.3)', minWidth: 100 }}>{item.label}</span>
                    <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.6)' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Version control note */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.12)' }}>
              <p className="font-mono text-[7px] uppercase tracking-widest mb-1" style={{ color: 'rgba(45,122,71,0.5)' }}>Version Control</p>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 9, lineHeight: 1.6 }}>
                This submission will be recorded as v1.0. Community remixes increment the minor version (v1.1, v1.2). Community review revisions increment the major version (v2.0). All versions are preserved in the archive.
              </p>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(3)} className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded" style={{ border: '1px solid rgba(61,54,39,0.3)', color: 'rgba(212,197,169,0.4)', cursor: 'pointer', background: 'transparent' }}>← Back</button>
              <button
                onClick={handleSubmit}
                disabled={!authorName}
                className="font-mono text-[9px] uppercase tracking-widest px-5 py-2 rounded transition-all"
                style={{
                  background: authorName ? 'rgba(45,122,71,0.12)' : 'rgba(61,54,39,0.2)',
                  border: `1px solid ${authorName ? 'rgba(45,122,71,0.4)' : 'rgba(61,54,39,0.3)'}`,
                  color: authorName ? '#2d7a47' : 'rgba(212,197,169,0.25)',
                  cursor: authorName ? 'pointer' : 'not-allowed',
                }}
              >
                Submit to Community Library ◎
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── COMMUNITY ROLE LIBRARY ───────────────────────────────────────────────────

function VersionHistory({ versions, color }: { versions: RoleVersion[]; color: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(v => !v)}
        className="font-mono text-[7px] uppercase tracking-widest flex items-center gap-1 transition-all"
        style={{ color: `${color}60`, background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <span style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▶</span>
        {versions.length} version{versions.length !== 1 ? 's' : ''} in archive
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {versions.map(v => (
            <div key={v.version} className="rounded px-2 py-1.5" style={{ background: `${color}05`, border: `1px solid ${color}12` }}>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-mono text-[8px] font-black" style={{ color }}>{v.version}</span>
                <span className="font-mono text-[7px]" style={{ color: 'rgba(212,197,169,0.3)' }}>{new Date(v.timestamp * 1000).toLocaleDateString()}</span>
                <span className="font-mono text-[7px]" style={{ color: 'rgba(212,197,169,0.4)' }}>by {v.author}</span>
              </div>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 9, lineHeight: 1.5 }}>{v.changes}</p>
              <p style={{ color: 'rgba(212,197,169,0.25)', fontSize: 8, fontStyle: 'italic' }}>{v.attribution}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunityRoleLibrary() {
  const [filter, setFilter] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const statusColors: Record<PublishedRole['status'], string> = {
    draft: '#5a5a8a',
    submitted: '#D4A82A',
    'community-review': '#2d8a87',
    published: '#2d7a47',
  };

  const filtered = filter === 'all' ? COMMUNITY_LIBRARY : COMMUNITY_LIBRARY.filter(r => r.baseRole === filter);

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter('all')} className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all" style={{ background: filter === 'all' ? 'rgba(201,168,76,0.1)' : 'transparent', border: `1px solid ${filter === 'all' ? 'rgba(201,168,76,0.35)' : 'rgba(61,54,39,0.3)'}`, color: filter === 'all' ? '#c9a84c' : 'rgba(212,197,169,0.4)', cursor: 'pointer' }}>All Roles</button>
        {SEED_ROLES.map(r => (
          <button key={r.id} onClick={() => setFilter(r.id)} className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all" style={{ background: filter === r.id ? `${r.color}12` : 'transparent', border: `1px solid ${filter === r.id ? `${r.color}35` : 'rgba(61,54,39,0.3)'}`, color: filter === r.id ? r.color : 'rgba(212,197,169,0.4)', cursor: 'pointer' }}>{r.label}</button>
        ))}
      </div>

      {/* Role cards */}
      <div className="space-y-3">
        {filtered.map(role => {
          const baseRole = SEED_ROLES.find(r => r.id === role.baseRole);
          const isExpanded = expandedId === role.id;
          return (
            <div key={role.id} className="rounded-xl overflow-hidden" style={{ border: `1px solid ${role.lineageColor}20` }}>
              <div className="p-4" style={{ background: `${role.lineageColor}06` }}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span style={{ color: role.lineageColor, fontSize: 14 }}>{baseRole?.icon}</span>
                      <p className="font-mono text-[10px] font-black uppercase tracking-wide" style={{ color: '#d4c5a9' }}>{role.customLabel}</p>
                      <span className="font-mono text-[7px] uppercase px-1.5 py-0.5 rounded" style={{ background: `${statusColors[role.status]}10`, color: statusColors[role.status], border: `1px solid ${statusColors[role.status]}20` }}>{role.status}</span>
                    </div>
                    <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10, lineHeight: 1.5, fontStyle: 'italic' }}>{role.vocalLens}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono text-xs font-black" style={{ color: role.lineageColor }}>{role.version}</div>
                    <div className="font-mono text-[7px]" style={{ color: 'rgba(212,197,169,0.3)' }}>{role.remixCount} remixes</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {role.keysEngaged.slice(0, 2).map(k => (
                    <span key={k} className="font-mono text-[7px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.06)', color: 'rgba(201,168,76,0.5)', border: '1px solid rgba(201,168,76,0.12)' }}>{k.split(':')[0]}</span>
                  ))}
                  {role.mrgDomains.slice(0, 2).map(d => (
                    <span key={d} className="font-mono text-[7px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(45,122,71,0.06)', color: 'rgba(45,122,71,0.5)', border: '1px solid rgba(45,122,71,0.12)' }}>{d}</span>
                  ))}
                  <button onClick={() => setExpandedId(isExpanded ? null : role.id)} className="font-mono text-[7px] uppercase tracking-widest ml-auto" style={{ color: `${role.lineageColor}60`, background: 'none', border: 'none', cursor: 'pointer' }}>
                    {isExpanded ? '▲ Less' : '▼ More'}
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-3" style={{ borderTop: `1px solid ${role.lineageColor}10` }}>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6, marginTop: 12 }}>{role.description}</p>

                  {/* Isnad chain */}
                  <div>
                    <p className="font-mono text-[7px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.4)' }}>Isnad Chain</p>
                    <div className="space-y-1">
                      {role.isnadChain.map((link, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="font-mono text-[7px]" style={{ color: 'rgba(201,168,76,0.35)' }}>{i + 1}.</span>
                          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9 }}>{link}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Custom fields */}
                  {Object.keys(role.customFields).length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(role.customFields).map(([k, v]) => (
                        <div key={k} className="rounded px-2 py-1.5" style={{ background: 'rgba(61,54,39,0.15)', border: '1px solid rgba(61,54,39,0.25)' }}>
                          <p className="font-mono text-[7px] uppercase" style={{ color: 'rgba(212,197,169,0.3)' }}>{k}</p>
                          <p className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.6)' }}>{v}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Version history */}
                  <VersionHistory versions={role.versions} color={role.lineageColor} />

                  {/* Remix button */}
                  <button className="font-mono text-[8px] uppercase tracking-widest px-3 py-1.5 rounded transition-all" style={{ background: `${role.lineageColor}08`, border: `1px solid ${role.lineageColor}25`, color: role.lineageColor, cursor: 'pointer' }}>
                    ⌘ Remix this Role
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── COMBINED PANEL ───────────────────────────────────────────────────────────

export default function RolePublishingPanel() {
  const [activeTab, setActiveTab] = useState<'publish' | 'library'>('publish');

  return (
    <div className="space-y-4">
      {/* Tab selector */}
      <div className="flex gap-2">
        {[
          { id: 'publish', label: 'Publish a Role' },
          { id: 'library', label: 'Community Library' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className="font-mono text-[8px] uppercase tracking-widest px-4 py-2 rounded transition-all"
            style={{
              background: activeTab === tab.id ? 'rgba(201,168,76,0.1)' : 'transparent',
              border: `1px solid ${activeTab === tab.id ? 'rgba(201,168,76,0.35)' : 'rgba(61,54,39,0.3)'}`,
              color: activeTab === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.4)',
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'publish' && <RolePublishingForm />}
      {activeTab === 'library' && (
        <div>
          <div className="mb-4">
            <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>◈ Community Role Library</p>
            <h3 className="font-mono text-base font-black uppercase tracking-tight mb-1" style={{ color: '#F5F0E8' }}>Published Role Lenses</h3>
            <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, lineHeight: 1.6 }}>
              Community-published role lenses with Isnad attribution chains and version history. Each role is a remix of a seed role, filtered through a specific vocational lens.
            </p>
          </div>
          <CommunityRoleLibrary />
        </div>
      )}
    </div>
  );
}
